/**
 * Notes de version publiées aux clients — données et modèle.
 *
 * Écrit par l'expert `.agent/workflows/release-notes-expert.md`, invoqué à la
 * main JUSTE AVANT un déploiement Clever Cloud, et validé par le PO avant
 * écriture. Rien n'atterrit ici sans son « je valide ».
 *
 * ── POURQUOI CE FICHIER EXISTE, ET PAS UN TABLEAU DANS LA PAGE ──────────────
 * `frontend/src/data/` est le SEUL répertoire exempté du plafond de 300 lignes
 * (`scripts/verify-file-sizes.sh`, sauts aux lignes 72 et 132). Trois notes de
 * version bilingues suffisent à faire sauter ce plafond dans un composant.
 * L'expert a en plus besoin d'une ancre d'écriture stable, loin du JSX.
 *
 * ── LA RÈGLE QUI GOUVERNE TOUT LE FICHIER ───────────────────────────────────
 * Le lecteur est le dirigeant ou l'assistant d'un organisme de formation. Il
 * connaît ses écrans, pas leur implémentation. On ne nomme JAMAIS ici : une
 * table, un moteur, un modèle d'isolation, un mécanisme de sécurité, une dette,
 * ni rien qui ne soit atteignable par un écran.
 */

/**
 * Un texte publié, dans les deux langues.
 *
 * ⚠️ `en` est REQUIS, et c'est délibéré : le gardien i18n de la CI est
 * structurellement aveugle sur ce fichier (sa passe bloquante ne lit que quatre
 * attributs HTML entre guillemets doubles ; le français vit ici dans des
 * littéraux qu'elle ne regarde pas). Aucune machine ne verra un oubli de
 * traduction — sauf le compilateur. Rendre `en` optionnel échangerait le
 * gardien le plus dur du dépôt contre une bonne intention.
 */
export interface Texte {
    fr: string
    en: string
}

/**
 * Renvoi vers un article du centre d'aide.
 *
 * ⚠️ C'est le champ `slug:` du FRONTMATTER de l'article, JAMAIS son nom de
 * fichier — les deux diffèrent sur la quasi-totalité du corpus.
 *
 * ⚠️ Les deux langues sont requises. Un renvoi qui n'existe qu'en français
 * ouvre, pour un lecteur en anglais, un volet qui ne montre rien : côté
 * serveur, un slug introuvable rend 404, et le volet AVALE ce 404. Le gardien
 * `verify-release-notes.mjs` refuse tout slug absent de l'un des deux corpus.
 */
export interface Aide {
    fr: string
    en: string
}

/** Étiquette de formule, affichée UNIQUEMENT dans le tiroir de pied de carte. */
export type Formule = 'boost' | 'business'

interface ItemBase {
    titre: Texte
    /**
     * Id de la capacité qui garde cet élément (`hasFeature('TENDERS')`).
     * Absent = accessible à toutes les formules.
     *
     * ⚠️ C'est CE champ qui décide de l'affichage, jamais `formule` : un tenant
     * Focus peut posséder une capacité Boost achetée à l'unité
     * (`stripe_addons`) ou accordée à la main (`manual_grants`). La formule ne
     * décrit pas ce que le client possède ; seul `effective_features` fait foi.
     */
    feature?: string
    /**
     * Étiquette affichée dans le tiroir. Obligatoire dès qu'il y a `feature`,
     * refusé sinon (gardien `verify-release-notes.mjs`). C'est une donnée
     * ÉCRITE, pas une déduction : la page ne lit jamais le palier du tenant.
     */
    formule?: Formule
}

/** Forme courte : un titre de quatre ou cinq mots, et rien d'autre. */
export interface ItemCourt extends ItemBase {
    phrase?: never
    aide?: never
    vedette?: never
}

/**
 * Forme détaillée : titre + une phrase de 15 à 20 mots, et éventuellement un
 * renvoi vers l'article d'aide.
 *
 * L'union avec `ItemCourt` rend `{ titre, aide }` sans phrase INCOMPILABLE :
 * la règle « jamais de renvoi sous un titre nu » cesse d'être une consigne.
 */
export interface ItemDetaille extends ItemBase {
    phrase: Texte
    aide?: Aide
    /**
     * La « vedette » de la version : UN seul élément hors formule par note est
     * promu en tête du tiroir, avec sa phrase et son renvoi. Les autres y
     * figurent en titres nus.
     *
     * Pourquoi une seule : une carte doit porter un argument, pas six. Le
     * tiroir reste borné quelle que soit la taille de la version.
     */
    vedette?: true
}

export type Item = ItemCourt | ItemDetaille

/** Les cinq rubriques. Une rubrique vide n'est pas rendue. */
export interface Release {
    version: string
    /** ISO 8601. La date du DÉPLOIEMENT réel, jamais celle de la rédaction. */
    date: string
    nouveautes?: Item[]
    ameliorations?: Item[]
    performance?: Item[]
    correctifs?: Item[]
    securite?: Item[]
}

/**
 * ── PLAFONDS, tenus par `verify-release-notes.mjs` ──────────────────────────
 *   · 3 phrases et 2 renvois par version, toutes rubriques confondues
 *   · 1 vedette par version au maximum
 *   · titre ≤ 8 mots · phrase entre 10 et 25 mots
 *
 * Ce ne sont pas des consignes de rédaction mais des assertions. Un rédacteur
 * relancé à chaque déploiement enrichit de plus en plus, et la dérive ne se
 * voit qu'en effet AGRÉGÉ sur la carte — jamais élément par élément.
 *
 * ── ORDRE ───────────────────────────────────────────────────────────────────
 * La plus récente EN PREMIER : la page décerne « Actuelle » à `releases[0]`.
 */
export const releases: Release[] = [
    {
        // Fenêtre : du déploiement du 30/08 (all-69cad038) au « Deploy all » du 22/10. Proposition
        // `.agent/syntheses/notes-de-version/2026-10-22-proposition.md`, validée par le PO le 22/10.
        version: '1.1.0',
        date: '2026-10-22',
        nouveautes: [
            {
                titre: { fr: "Répondez aux appels d'offres depuis Argalis", en: 'Respond to public tenders from Argalis' },
                phrase: {
                    fr: 'Chaque matin, la veille vous apporte les avis du BOAMP qui correspondent à vos critères, et leur fiche réunit pièces, échéances et mémoire technique.',
                    en: 'Every morning, the watch brings you public tender notices matching your criteria, each with a record for documents, deadlines and your technical proposal.',
                },
                aide: { fr: 'appels-offres-introduction', en: 'appels-offres-introduction' },
                feature: 'TENDERS',
                formule: 'business',
                vedette: true,
            },
            {
                titre: { fr: 'Tenez vos checklists de suivi sur chaque fiche', en: 'Keep follow-up checklists on every record' },
                phrase: {
                    fr: 'Vous cochez chaque point sur la fiche concernée, pièce justificative jointe, et téléchargez le dossier de preuve pour votre audit.',
                    en: 'You tick each item on the relevant record, attach the supporting document, and download the evidence file for your audit.',
                },
                aide: { fr: 'checklists-modeles', en: 'checklists-modeles' },
            },
            {
                titre: { fr: 'Gérez votre matériel dans Argalis', en: 'Manage your equipment in Argalis' },
                phrase: {
                    fr: 'Du harnais au chariot élévateur, vous suivez les vérifications réglementaires de chaque matériel et sortez votre registre de sécurité daté.',
                    en: 'From harnesses to forklifts, you track the regulatory inspections of each item and export your dated safety register.',
                },
            },
            {
                titre: { fr: 'Réservez salles et matériel, évitez les doubles réservations', en: 'Book rooms and equipment, avoid double bookings' },
            },
            {
                titre: { fr: 'Ajoutez vos propres champs à vos fiches', en: 'Add your own fields to your records' },
                feature: 'CUSTOM_FIELDS',
                formule: 'boost',
            },
            {
                titre: { fr: 'Exigez les champs que vos fiches doivent porter', en: 'Require the fields your records must include' },
                feature: 'REQUIRED_FIELDS',
                formule: 'business',
            },
        ],
        correctifs: [
            {
                titre: { fr: "Vos notifications ouvrent l'élément qu'elles annoncent", en: 'Your notifications open the item they announce' },
            },
        ],
    },
]
